import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Form,
} from 'react-bootstrap';
import CheckboxSelector from './index';

// Mock data for demonstration
const cityOptions = [
  { label: 'São Paulo', value: 'sao-paulo' },
  { label: 'Rio de Janeiro', value: 'rio-de-janeiro' },
  { label: 'Belo Horizonte', value: 'belo-horizonte' },
  { label: 'Salvador', value: 'salvador' },
  { label: 'Fortaleza', value: 'fortaleza' },
  { label: 'Brasília', value: 'brasilia' },
  { label: 'Curitiba', value: 'curitiba' },
  { label: 'Recife', value: 'recife' },
  { label: 'Porto Alegre', value: 'porto-alegre' },
  { label: 'Manaus', value: 'manaus' },
  { label: 'Belém', value: 'belem' },
  { label: 'Goiânia', value: 'goiania' },
  { label: 'Campinas', value: 'campinas' },
  { label: 'São Luís', value: 'sao-luis' },
  { label: 'Maceió', value: 'maceio' },
];

const skillOptions = [
  { label: 'JavaScript', value: 'javascript' },
  { label: 'TypeScript', value: 'typescript' },
  { label: 'React', value: 'react' },
  { label: 'Vue.js', value: 'vue' },
  { label: 'Angular', value: 'angular' },
  { label: 'Node.js', value: 'nodejs' },
  { label: 'Python', value: 'python' },
  { label: 'Java', value: 'java' },
  { label: 'C#', value: 'csharp' },
  { label: 'PHP', value: 'php' },
  { label: 'Go', value: 'go' },
  { label: 'Rust', value: 'rust' },
];

const categoryOptions = [
  { label: 'Frontend', value: 'frontend' },
  { label: 'Backend', value: 'backend' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'DevOps', value: 'devops' },
  { label: 'Data Science', value: 'data-science' },
  { label: 'Machine Learning', value: 'machine-learning' },
  { label: 'UI/UX Design', value: 'ui-ux' },
  { label: 'Quality Assurance', value: 'qa' },
  { label: 'Database', value: 'database' },
  { label: 'Security', value: 'security' },
];

/**
 * Componente de demonstração do CheckboxSelector
 * Mostra como usar o componente em diferentes cenários
 */
export default function CheckboxSelectorDemo() {
  // Estados para diferentes exemplos
  const [selectedCities, setSelectedCities] = useState<readonly string[]>([
    'sao-paulo',
    'rio-de-janeiro',
  ]);
  const [selectedSkills, setSelectedSkills] = useState<readonly string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<readonly string[]>([
    'frontend',
    'backend',
  ]);

  // Estados para filtros/busca
  const [citySearch, setCitySearch] = useState('');
  const [skillSearch, setSkillSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  // Filtrar opções baseado na busca
  const filteredCities = cityOptions.filter((city) => (
    city.label.toLowerCase().includes(citySearch.toLowerCase())
  ));

  const filteredSkills = skillOptions.filter((skill) => (
    skill.label.toLowerCase().includes(skillSearch.toLowerCase())
  ));

  const filteredCategories = categoryOptions.filter((category) => (
    category.label.toLowerCase().includes(categorySearch.toLowerCase())
  ));

  const handleReset = () => {
    setSelectedCities([]);
    setSelectedSkills([]);
    setSelectedCategories([]);
    setCitySearch('');
    setSkillSearch('');
    setCategorySearch('');
  };

  const handleSubmit = () => {
    // eslint-disable-next-line no-console
    console.log('Seleções:', {
      cities: selectedCities,
      skills: selectedSkills,
      categories: selectedCategories,
    });

    // eslint-disable-next-line no-alert
    alert('Veja o console para as seleções!');
  };

  return (
    <Container className="py-4">
      <h1>Demo: CheckboxSelector Component</h1>
      <p>
        Demonstração do componente CheckboxSelector com diferentes cenários de uso.
      </p>

      <hr />

      {/* Botões de Ação */}
      <Row className="mb-4">
        <Col>
          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={handleSubmit}
          >
            Ver Seleções (Console)
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleReset}
          >
            Limpar Todos
          </button>
        </Col>
      </Row>

      <Row>
        {/* Exemplo 1: Cidades */}
        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Seleção de Cidades</h5>
              <small className="text-muted">
                {selectedCities.length}
                {' '}
                de
                {' '}
                {cityOptions.length}
                {' '}
                selecionadas
              </small>
            </Card.Header>
            <Card.Body>
              {/* Campo de busca */}
              <Form.Group className="mb-3">
                <Form.Label>Buscar cidade:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome da cidade"
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                />
              </Form.Group>

              {/* CheckboxSelector */}
              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  padding: '10px',
                  borderRadius: '5px',
                }}
              >
                <CheckboxSelector
                  itemCount={cityOptions.length}
                  filteredItems={filteredCities}
                  selectedItems={Array.from(selectedCities)}
                  setValue={setSelectedCities}
                  searchTerm={citySearch}
                  selectAllLabel="Selecionar Todas as Cidades"
                  selectedLabel="Cidades Selecionadas"
                  unselectedLabel="Cidades Disponíveis"
                />
              </div>

              {filteredCities.length === 0 && citySearch && (
                <div className="text-center text-muted mt-2">
                  <small>Nenhuma cidade encontrada</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Exemplo 2: Habilidades */}
        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Habilidades Técnicas</h5>
              <small className="text-muted">
                {selectedSkills.length}
                {' '}
                de
                {' '}
                {skillOptions.length}
                {' '}
                selecionadas
              </small>
            </Card.Header>
            <Card.Body>
              {/* Campo de busca */}
              <Form.Group className="mb-3">
                <Form.Label>Buscar tecnologia:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome da tecnologia"
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                />
              </Form.Group>

              {/* CheckboxSelector */}
              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  padding: '10px',
                  borderRadius: '5px',
                }}
              >
                <CheckboxSelector
                  itemCount={skillOptions.length}
                  filteredItems={filteredSkills}
                  selectedItems={Array.from(selectedSkills)}
                  setValue={setSelectedSkills}
                  searchTerm={skillSearch}
                  selectAllLabel="Selecionar Todas"
                  selectedLabel="Minhas Habilidades"
                  unselectedLabel="Outras Tecnologias"
                />
              </div>

              {filteredSkills.length === 0 && skillSearch && (
                <div className="text-center text-muted mt-2">
                  <small>Nenhuma tecnologia encontrada</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Exemplo 3: Categorias */}
        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Áreas de Interesse</h5>
              <small className="text-muted">
                {selectedCategories.length}
                {' '}
                de
                {' '}
                {categoryOptions.length}
                {' '}
                selecionadas
              </small>
            </Card.Header>
            <Card.Body>
              {/* Campo de busca */}
              <Form.Group className="mb-3">
                <Form.Label>Buscar área:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome da área"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
              </Form.Group>

              {/* CheckboxSelector */}
              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  padding: '10px',
                  borderRadius: '5px',
                }}
              >
                <CheckboxSelector
                  itemCount={categoryOptions.length}
                  filteredItems={filteredCategories}
                  selectedItems={Array.from(selectedCategories)}
                  setValue={setSelectedCategories}
                  searchTerm={categorySearch}
                  selectAllLabel="Interessado em Todas"
                  selectedLabel="Minhas Áreas"
                  unselectedLabel="Outras Áreas"
                />
              </div>

              {filteredCategories.length === 0 && categorySearch && (
                <div className="text-center text-muted mt-2">
                  <small>Nenhuma área encontrada</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <hr className="my-5" />

      {/* Resumo das Seleções */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Resumo das Seleções</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <h6>Cidades Selecionadas:</h6>
                  {selectedCities.length > 0 ? (
                    <ul className="list-unstyled">
                      {selectedCities.map((cityValue) => {
                        const city = cityOptions.find((c) => c.value === cityValue);
                        return (
                          <li key={cityValue}>
                            <span className="badge bg-primary me-1">{city?.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-muted">Nenhuma cidade selecionada</p>
                  )}
                </Col>

                <Col md={4}>
                  <h6>Habilidades Selecionadas:</h6>
                  {selectedSkills.length > 0 ? (
                    <ul className="list-unstyled">
                      {selectedSkills.map((skillValue) => {
                        const skill = skillOptions.find((s) => s.value === skillValue);
                        return (
                          <li key={skillValue}>
                            <span className="badge bg-success me-1">{skill?.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-muted">Nenhuma habilidade selecionada</p>
                  )}
                </Col>

                <Col md={4}>
                  <h6>Áreas de Interesse:</h6>
                  {selectedCategories.length > 0 ? (
                    <ul className="list-unstyled">
                      {selectedCategories.map((categoryValue) => {
                        const category = categoryOptions.find((c) => c.value === categoryValue);
                        return (
                          <li key={categoryValue}>
                            <span className="badge bg-info me-1">{category?.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-muted">Nenhuma área selecionada</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <hr className="my-5" />

      <div className="alert alert-info">
        <h4>Como o CheckboxSelector Funciona:</h4>
        <ol>
          <li>
            <strong>Organização Visual:</strong>
            {' '}
            Os checkboxes são separados em duas seções:
            {' '}
            &quot;Selecionados&quot; e &quot;Disponíveis&quot;
          </li>
          <li>
            <strong>Select All:</strong>
            {' '}
            Aparece apenas quando não há busca ativa (searchTerm === &apos;&apos;)
          </li>
          <li>
            <strong>Selecionados:</strong>
            {' '}
            Itens marcados aparecem no topo, facilitando a remoção
          </li>
          <li>
            <strong>Disponíveis:</strong>
            {' '}
            Itens não selecionados aparecem na seção inferior
          </li>
          <li>
            <strong>Busca:</strong>
            {' '}
            Filtra os itens em tempo real e oculta o
            {' '}
            &quot;Select All&quot;
          </li>
          <li>
            <strong>Estado Visual:</strong>
            {' '}
            Checkboxes mostram corretamente se estão marcados ou não
          </li>
        </ol>

        <h6>Exemplo de uso no código:</h6>
        <pre
          className="bg-light p-2 rounded mt-3"
          style={{ fontSize: '0.8rem' }}
        >
          {`<CheckboxSelector
  itemCount={options.length}
  filteredItems={filteredOptions}
  selectedItems={selectedItems}
  setValue={setSelectedItems}
  searchTerm={searchTerm}
  selectAllLabel="Selecionar Todos"
  selectedLabel="Selecionados"
  unselectedLabel="Disponíveis"
/>`}
        </pre>
      </div>
    </Container>
  );
}
